import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone, Loader2, Send, AlertTriangle, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` });

export const ShelfAnnounceCard = () => {
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [audience, setAudience] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadFolders = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/admin/bdm-shelf`, { headers: authHeader() }),
        axios.get(`${API}/admin/shelf-announcements?limit=6`, { headers: authHeader() }),
      ]);
      const shared = (s.data?.folders || []).filter((f) => f.bdm_visible);
      setFolders(shared);
      setHistory(h.data || []);
      if (shared.length && !folderId) setFolderId(shared[0].id);
    } catch {
      setFolders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  useEffect(() => {
    if (!folderId) { setAudience(null); return; }
    axios
      .get(`${API}/admin/bdm-shelf/${folderId}/audience`, { headers: authHeader() })
      .then((r) => {
        setAudience(r.data);
        setNote(r.data.shelf_note || "");
      })
      .catch(() => setAudience(null));
  }, [folderId]);

  const send = async () => {
    const count = audience?.recipients?.length || 0;
    if (!window.confirm(`Email ${count} partner${count === 1 ? "" : "s"} about “${audience?.folder_name}”?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/bdm-shelf/${folderId}/announce`,
        { note },
        { headers: authHeader() },
      );
      if (data.failed?.length) {
        toast.error(`${data.sent.length} sent, ${data.failed.length} failed — check the addresses.`);
      } else {
        toast.success(`Announced to ${data.sent.length} partner${data.sent.length === 1 ? "" : "s"}`);
      }
      loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send that.");
    } finally {
      setBusy(false);
    }
  };

  if (folders.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5" data-testid="comms-shelf-announce">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
          <Megaphone size={13} /> Shelf drop
        </div>
        <h3 className="mt-1 font-heading font-medium text-xl text-navy">Nothing shared yet</h3>
        <p className="mt-1 text-sm text-ink-500">
          Tag a library folder for partners in <strong>Partner team → Partner shelf</strong>, then you can
          announce it here.
        </p>
      </div>
    );
  }

  const recipients = audience?.recipients || [];
  const missingLink = recipients.filter((r) => !r.has_link).length;

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5" data-testid="comms-shelf-announce">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <Megaphone size={13} /> Shelf drop
      </div>
      <h3 className="mt-1 font-heading font-medium text-xl text-navy">Tell the partners it's there</h3>
      <p className="mt-1 text-sm text-ink-500">
        Tagging a folder puts it on their dashboard quietly. This is the nudge that gets them to open it —
        it goes only to the partners that folder already reaches, each with their own private link.
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">Which folder</span>
          <select
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            data-testid="comms-announce-folder"
            className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white focus:border-teal focus:outline-none"
          >
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.share_name || f.name} {f.drop_month ? `· ${f.drop_month}` : ""} ({f.asset_count})
              </option>
            ))}
          </select>
        </label>
        <div className="rounded-xl bg-linen/70 border border-linen-border p-3" data-testid="comms-announce-audience">
          <p className="text-xs uppercase tracking-widest text-ink-500">Reaches</p>
          {audience ? (
            <>
              <p className="mt-1 text-sm text-navy font-medium">
                {recipients.length} partner{recipients.length === 1 ? "" : "s"} · {audience.asset_count} item
                {audience.asset_count === 1 ? "" : "s"}
              </p>
              <p className="text-[11px] text-ink-500 mt-0.5 truncate" title={recipients.map((r) => r.email).join(", ")}>
                {recipients.length ? recipients.map((r) => r.name || r.email).join(", ") : "Nobody — the audience tag is empty."}
              </p>
              {audience.announced_at && (
                <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-teal">
                  <Check className="w-3 h-3" /> Last announced {new Date(audience.announced_at).toLocaleDateString()} to{" "}
                  {audience.announced_count}
                </p>
              )}
              {missingLink > 0 && (
                <p className="mt-1.5 inline-flex items-start gap-1 text-[11px] text-amber-700">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  {missingLink} without a private link — they'll get the password sign-in instead
                </p>
              )}
            </>
          ) : (
            <Loader2 className="mt-2 w-4 h-4 animate-spin text-teal" />
          )}
        </div>
      </div>

      <label className="block mt-4">
        <span className="text-xs uppercase tracking-widest text-ink-500">
          How to use these <span className="normal-case tracking-normal text-ink-400">(appears in the email — defaults to the folder's shelf note)</span>
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          data-testid="comms-announce-note"
          placeholder="e.g. Post two of these a week — captions are in each file description."
          className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
        />
      </label>

      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          onClick={send}
          disabled={busy || recipients.length === 0}
          data-testid="comms-announce-send"
          className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Announce to {recipients.length} partner{recipients.length === 1 ? "" : "s"}
        </button>
      </div>

      {history.length > 0 && (
        <div className="mt-5 pt-4 border-t border-linen-border" data-testid="comms-announce-history">
          <p className="text-xs uppercase tracking-widest text-ink-500">Recent drops</p>
          <ul className="mt-2 space-y-1">
            {history.map((h, i) => (
              <li key={i} className="text-xs text-ink-500">
                {(h.sent_at || "").slice(0, 10)} — <span className="text-navy">{h.folder_name}</span> to {h.sent} partner
                {h.sent === 1 ? "" : "s"}
                {h.failed ? <span className="text-red-600"> · {h.failed} failed</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ShelfAnnounceCard;
